import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import axios from "axios";
import { SEO } from "@/components/SEO";
import { logError } from "@/lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mdComponents = {
  h2: ({ node, ...props }) => <h2 className="text-2xl md:text-3xl font-serif tracking-tight text-foreground mt-12 mb-4" {...props} />,
  h3: ({ node, ...props }) => <h3 className="text-xl md:text-2xl font-serif text-foreground mt-8 mb-3" {...props} />,
  p: ({ node, ...props }) => <p className="text-base font-sans text-foreground/80 leading-relaxed mb-5" {...props} />,
  ul: ({ node, ...props }) => <ul className="space-y-3 mb-6" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal space-y-3 mb-6 pl-5" {...props} />,
  li: ({ node, ...props }) => (
    <li className="text-base font-sans text-foreground/80 leading-relaxed pl-5 relative before:content-[''] before:absolute before:left-0 before:top-[11px] before:w-2.5 before:h-[2px] before:bg-[#C5A059]" {...props} />
  ),
  strong: ({ node, ...props }) => <strong className="font-semibold text-foreground" {...props} />,
  em: ({ node, ...props }) => <em className="text-muted-foreground" {...props} />,
  a: ({ node, ...props }) => <a className="text-[#C5A059] underline underline-offset-4 hover:text-foreground" target="_blank" rel="noopener noreferrer" {...props} />,
  blockquote: ({ node, ...props }) => <blockquote className="border-l-2 border-[#C5A059] pl-6 py-1 my-8 font-serif text-lg text-foreground/70 italic" {...props} />,
  table: ({ node, ...props }) => (
    <div className="overflow-x-auto mb-8">
      <table className="w-full border-collapse text-sm font-sans" {...props} />
    </div>
  ),
  th: ({ node, ...props }) => <th className="border border-border bg-background px-4 py-3 text-left font-medium text-foreground" {...props} />,
  td: ({ node, ...props }) => <td className="border border-border px-4 py-3 text-foreground/80" {...props} />,
};

function buildArticleJsonLd(post) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.meta_description || post.excerpt,
    image: post.cover_image,
    author: { "@type": "Organization", name: post.author },
    publisher: {
      "@type": "Organization",
      name: "KF Prints Pvt. Ltd.",
      logo: { "@type": "ImageObject", url: `${window.location.origin}/kf-logo.png` },
    },
    datePublished: post.created_at,
    dateModified: post.updated_at,
    mainEntityOfPage: `${window.location.origin}/blog/${post.slug || post.id}`,
  };
}

function BlogArticle({ post }) {
  return (
    <article className="py-24 lg:py-32">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-sans text-muted-foreground hover:text-foreground mb-8" data-testid="blog-back-link">
          <ArrowLeft size={14} /> Back to Home
        </Link>
        {post.cover_image && (
          <div className="aspect-[16/9] overflow-hidden mb-10">
            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}
        <p className="text-xs font-sans uppercase tracking-[0.2em] text-[#C5A059] mb-4">{post.category}</p>
        <h1 className="text-4xl md:text-5xl font-serif tracking-tight text-foreground mb-6">{post.title}</h1>
        <p className="text-sm font-sans text-muted-foreground mb-12 pb-8 border-b border-border">
          By {post.author} &middot; {new Date(post.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </p>
        <div data-testid="blog-post-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
            {post.content}
          </ReactMarkdown>
        </div>
      </div>
    </article>
  );
}

export default function BlogPostPage() {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    axios.get(`${API}/blog/${postId}`)
      .then((r) => setPost(r.data))
      .catch((err) => logError("Failed to load blog post", err))
      .finally(() => setLoading(false));
  }, [postId]);

  if (loading) return <div className="pt-32 pb-32 text-center text-sm font-sans text-muted-foreground">Loading...</div>;
  if (!post) return <div className="pt-32 pb-32 text-center text-sm font-sans text-muted-foreground">Post not found.</div>;

  return (
    <div data-testid="blog-post-page" className="pt-20 lg:pt-24">
      <SEO
        title={`${post.title} | KF Prints Insights`}
        description={post.meta_description || post.excerpt}
        keywords={(post.tags || []).join(", ")}
        image={post.cover_image}
        type="article"
        path={`/blog/${post.slug || post.id}`}
        jsonLd={buildArticleJsonLd(post)}
      />
      <BlogArticle post={post} />
    </div>
  );
}
